'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { config } from '@/config';
import { getBootId, getBootTimestamp } from '@/core/utils/bootSession';

// List of paths that should have Sentry enabled
// Includes auth pages so login/signup errors are captured in production
const SENTRY_ENABLED_PATHS = [
  '/dashboard',
  '/conversations',
  '/contacts',
  '/settings',
  '/teams',
  '/onboarding',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/signup',
  '/get-started',
];

let sentryInitialized = false;

export function ClientSentryProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  useEffect(() => {
    // Skip if already initialized - no work needed on subsequent route changes
    if (sentryInitialized) return;

    // Check if we should enable Sentry for this path
    const shouldEnable = SENTRY_ENABLED_PATHS.some(path => pathname?.startsWith(path));

    // Only initialize once on internal pages — dynamic import keeps @sentry/react
    // out of the initial bundle for auth/landing/onboarding routes
    if (shouldEnable && config.monitoring.sentry.dsn && config.monitoring.sentry.enabled) {
      sentryInitialized = true; // Set early to prevent duplicate loads

      import('@sentry/react').then((Sentry) => {
        Sentry.init({
          dsn: config.monitoring.sentry.dsn,
          environment: config.env,
          release: config.monitoring.sentry.release,

          // Minimal configuration for error tracking only
          tracesSampleRate: 0,
          replaysSessionSampleRate: 0,
          replaysOnErrorSampleRate: 0,

          // Only send errors and warnings (for RTKQ failures)
          beforeSend(event) {
            if (event.level === 'info' || event.level === 'debug') {
              return null;
            }
            return event;
          },

          // Breadcrumbs for error context + dedup to prevent duplicate events
          integrations: [
            Sentry.breadcrumbsIntegration({
              console: true,
              dom: true,
              fetch: true,
              xhr: true,
            }),
            Sentry.dedupeIntegration(),
          ],

          // Initial scope with boot session info
          initialScope: {
            tags: {
              boot_id: getBootId(),
            },
            extra: {
              boot_timestamp: getBootTimestamp(),
            },
          },
        });

        // Set boot_id as a global tag for all future events
        Sentry.setTag('boot_id', getBootId());

        // Expose Sentry on window for error boundaries and feedback button
        window.Sentry = {
          captureException: Sentry.captureException,
          captureMessage: Sentry.captureMessage,
          captureFeedback: Sentry.captureFeedback,
          withScope: Sentry.withScope as unknown as NonNullable<typeof window.Sentry>['withScope'],
          showReportDialog: () => {
            // User should use the Report Bug button to submit feedback
          },
          setUser: Sentry.setUser,
          setContext: Sentry.setContext,
          setTag: Sentry.setTag,
          setExtra: Sentry.setExtra,
          // Expose boot_id for debugging
          getBootId,
        };
      });
    }
  }, [pathname]);

  return <>{children}</>;
}